import React, { JSX, useRef } from 'react';
import { XYCoord, useDrop, useDrag } from 'react-dnd';
import { AvatarProps } from '@mui/material/Avatar';
import { IconType } from 'src/BaseComponents/Icon/Utils/IconDefinitions';
import EDDraggableChipView from './EDDraggableChipView';
import EDBox from '../../EDBox/EDBox';

interface IProps {
  /** Id för chippet */
  id: string | number;
  /** Texten som står i chippet, skickas även med till dnd men används ej där */
  name: string;
  /** Index i listan, används av dnd för att identifiera ett chip */
  index: number;
  /** Visa name som en tooltip när man hovrar över texten */
  showTooltip: boolean;
  /** Körs när man trycker på krysset */
  onDelete?: () => void;
  /** Körs när ett item ska flytta på sig */
  moveItem: (fromId: string, toIndex: number) => void;
  /** Körs när man klickar på chippet */
  onClick?: React.MouseEventHandler<any>;
  chipSelection?: boolean;
  onAvatarClick?: AvatarProps['onClick'];
  dragHandle?: boolean;
  avatarHighLighted?: boolean;
  avatarTooltip?: string;
  alternateAvatar?: IconType;
  clickDisabled?: boolean;
  labelIcon?: React.ReactNode;
  disableRipple?: boolean;
  showHidden?: boolean;
  hidden?: boolean;
  deleteIcon?: JSX.Element;
  hoverAvatar?: JSX.Element;
}

export interface IDraggableChipDragItem {
  id: string;
  index: IProps['index'];
  alternateAvatar: IProps['alternateAvatar'];
  width: number;
}

enum ItemTypes {
  CHIP = 'chip',
}

/**
 * Enstaka draggable, med Avatar/handle, tooltip och möjlighet till ikon.
 */
export const EDDraggableChip: React.FC<React.PropsWithChildren<IProps>> = (
  props: IProps
) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    IDraggableChipDragItem,
    void,
    { handlerId: string | symbol | null }
  >({
    accept: ItemTypes.CHIP,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: IDraggableChipDragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        if (hoverIndex - dragIndex > 1) {
          //the item is not already next to the hovered item because it has moved fast or around other items
          props.moveItem(item.id, hoverIndex - 1);
          item.index = hoverIndex - 1;
        }
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        if (dragIndex - hoverIndex > 1) {
          //the item is not already next to the hovered item because it has moved fast or around other items
          props.moveItem(item.id, hoverIndex + 1);
          item.index = hoverIndex + 1;
        }
        return;
      }

      // Time to actually perform the action
      props.moveItem(item.id, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CHIP,
    item: () => {
      let width = 200;

      if (ref.current) {
        width = ref.current.offsetWidth;
      }

      return {
        id: props.id,
        index: props.index,
        alternateAvatar: props.alternateAvatar,
        width,
      };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));
  return (
    <EDBox
      sx={{ marginY: 1, marginX: 0 }}
      ref={ref}
      data-handler-id={handlerId}
      testID={`draggableChip-${props.name}`}
    >
      <EDDraggableChipView
        deleteIcon={props.deleteIcon}
        showHidden={props.showHidden}
        hidden={props.hidden}
        isDragging={isDragging}
        name={props.name}
        showTooltip={props.showTooltip}
        onDelete={props.onDelete}
        onClick={props.onClick}
        chipSelection={props.chipSelection}
        onAvatarClick={props.onAvatarClick}
        dragHandle={props.dragHandle}
        avatarHighLighted={props.avatarHighLighted}
        avatarTooltip={props.avatarTooltip}
        alternateAvatar={props.alternateAvatar}
        clickDisabled={props.clickDisabled}
        labelIcon={props.labelIcon}
        disableRipple={props.disableRipple}
        hoverAvatar={props.hoverAvatar}
      />
    </EDBox>
  );
};

export default EDDraggableChip;
